import api from './api';

class AuthService {
  // Storage keys
  static TOKEN_KEY = 'access_token';
  static REFRESH_TOKEN_KEY = 'refresh_token';
  static USER_KEY = 'user_data';
  static USER_TYPE_KEY = 'user_type';

  // Login user
  static async login(credentials, rememberMe = false) {
    try {
        // Backend expects OAuth2 form data format for /auth/login
        const formData = new FormData();
        formData.append('username', credentials.username || credentials.email);
        formData.append('password', credentials.password);
        const response = await api.post('/api/v1/auth/login', formData, {
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
            }
        });
        const { access_token, refreshToken, user_type } = response.data;
        // Store tokens and user data
        const storage = rememberMe ? localStorage : sessionStorage;
        storage.setItem(this.TOKEN_KEY, access_token);
        if (refreshToken) {
            storage.setItem(this.REFRESH_TOKEN_KEY, refreshToken);
        }
        storage.setItem(this.USER_TYPE_KEY, user_type);
        return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: this.handleApiError(error)
      };
    }
  }

  // Register new user
  static async register(userData) {
    try {
      const response = await api.post('/api/v1/auth/register', userData);
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: this.handleApiError(error)
      };
    }
  }

  // Logout user
  static logout() {
    // Clear from both storages
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.REFRESH_TOKEN_KEY);
    localStorage.removeItem(this.USER_KEY);
    sessionStorage.removeItem(this.TOKEN_KEY);
    sessionStorage.removeItem(this.REFRESH_TOKEN_KEY);
    sessionStorage.removeItem(this.USER_KEY);
  }

  // Get stored token
  static getToken() {
      const bearer_token = localStorage.getItem(this.TOKEN_KEY) ||
           sessionStorage.getItem(this.TOKEN_KEY);
      return bearer_token;
  }

  // Remove token
  static removeToken() {
    localStorage.removeItem(this.TOKEN_KEY);
    sessionStorage.removeItem(this.TOKEN_KEY);
  }

  // Get stored user data
  static getUser() {
      return localStorage.getItem(this.USER_KEY) ||
          sessionStorage.getItem(this.USER_KEY);
  }
  static getUserType() {
      console.log("LS", localStorage.getItem(this.USER_TYPE_KEY))
      console.log("SS",sessionStorage.getItem(this.USER_TYPE_KEY))
      return localStorage.getItem(this.USER_TYPE_KEY) ||
          sessionStorage.getItem(this.USER_TYPE_KEY);
  }


  // Check if user is authenticated
  static isAuthenticated() {
    return !!this.getToken();
  }

  // Refresh token
  static async refreshToken() {
    try {
      const refreshToken = localStorage.getItem(this.REFRESH_TOKEN_KEY) ||
                          sessionStorage.getItem(this.REFRESH_TOKEN_KEY);
      
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      const response = await api.post('/api/v1/auth/refresh', { refreshToken });
      const { token } = response.data;

      // Update token in storage
      const storage = localStorage.getItem(this.TOKEN_KEY) ? localStorage : sessionStorage;
      storage.setItem(this.TOKEN_KEY, token);

      return { success: true, token };
    } catch (error) {
      this.logout();
      return { success: false, error: this.handleApiError(error) };
    }
  }

  // Handle API errors
  static handleApiError(error) {
    if (error.response) {
      // Server responded with error
      const { status, data } = error.response;
      switch (status) {
        case 400:
          return data.message || 'Invalid request data';
        case 401:
          return 'Invalid credentials';
        case 403:
          return 'Access denied';
        case 404:
          return 'Service not found';
        case 409:
          return data.message || 'Account already exists';
        case 500:
          return 'Server error. Please try again later.';
        default:
          return data.message || 'An unexpected error occurred';
      }
    } else if (error.request) {
      // Network error
      return 'Network error. Please check your connection.';
    } else {
      // Other error
      return error.message || 'An unexpected error occurred';
    }
  }

  // Validate email format
  static validateEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  // Validate password strength
  static validatePassword(password) {
    const minLength = 8;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumbers = /\d/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

    const errors = [];
    if (password.length < minLength) {
      errors.push(`Password must be at least ${minLength} characters long`);
    }
    if (!hasUpperCase) {
      errors.push('Password must contain at least one uppercase letter');
    }
    if (!hasLowerCase) {
      errors.push('Password must contain at least one lowercase letter');
    }
    if (!hasNumbers) {
      errors.push('Password must contain at least one number');
    }
    if (!hasSpecialChar) {
      errors.push('Password must contain at least one special character');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }
}

export default AuthService;